import { HttpErrorResponse, HttpResponse } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { Router } from '@angular/router'
import { BehaviorSubject, Observable, Subscriber, catchError, map } from 'rxjs'
import { StorageKeys } from 'src/app/domain/enums/application/storage-keys.enum'
import { ToastSeverity } from 'src/app/domain/enums/components/toast.enum'
import { AuthMethod } from 'src/app/domain/enums/erp/auth-method.enum'
import { Role } from 'src/app/domain/enums/erp/role.enum'
import { ApiResponse } from 'src/app/domain/interfaces/application/api-response.interface'
import { Toast } from 'src/app/domain/interfaces/components/toast.interface'
import { AuthUserCompany } from 'src/app/domain/models/application/auth-user-company.model'
import { Permission } from 'src/app/domain/models/erp/administrator/permission.model'
import { User } from 'src/app/domain/models/erp/administrator/user.model'
import { SystemModules } from 'src/app/domain/utils/constants/system-modules'
import { isExternalRoute } from 'src/app/domain/utils/functions/is-external-route'
import { TokenService } from 'src/app/infra/auth/token/token.service'
import { UserRepositoryService } from '../../../repositories/application/user/user-repository.service'
import { SplashScreenService } from '../../components/splash-screen/splash-screen.service'
import { ToastService } from '../../components/toast/toast.service'
import { CompanyService } from '../company/company.service'
import { PulsarWebSocketService } from '../pulsar-websocket/pulsar-websocket.service'

@Injectable({ providedIn: 'root' })
export class UserService {
  private userRepositoryService: UserRepositoryService = inject(UserRepositoryService)
  private splashScreenService: SplashScreenService = inject(SplashScreenService)
  private tokenService: TokenService = inject(TokenService)
  private router: Router = inject(Router)
  private toastService: ToastService = inject(ToastService)
  private pulsarWebSocketService: PulsarWebSocketService = inject(PulsarWebSocketService)
  private companyService: CompanyService = inject(CompanyService)

  companiesBehaviorSubject: BehaviorSubject<Array<AuthUserCompany>> = new BehaviorSubject(new Array())
  displayNameSubject: BehaviorSubject<string> = new BehaviorSubject<string>('')
  companies: Array<AuthUserCompany> = new Array()

  private currentScreenId: number = 0

  public setAuthUser(authUser: User): void {
    this.userRepositoryService.setAuthUser(authUser)
    this.displayNameSubject.next(authUser.fullName)
  }

  public setScreenId(screenId: number): void {
    this.currentScreenId = screenId
  }

  public getAuthUser(): User {
    return this.userRepositoryService.getAuthUser()
  }

  public getAuthUserId(): number {
    return this.userRepositoryService.getAuthUser().id
  }

  public getAuthUserDisplayName(): string {
    return this.userRepositoryService.getAuthUser().fullName
  }

  public getAuthUserEmail(): string {
    return this.userRepositoryService.getAuthUser().login
  }

  public getAuthUserForcePasswordReset(): boolean {
    return this.userRepositoryService.getAuthUser().forcePasswordReset
  }

  public getAuthUserApiToken(): string | null {
    return this.userRepositoryService.getAuthUser().apiToken
  }

  public getAuthUserPhone(): string {
    return this.userRepositoryService.getAuthUser().phone ?? ''
  }

  public getAuthUserCpf(): string {
    return this.userRepositoryService.getAuthUser().cpf ?? ''
  }

  public getAuthUserPreferredAuth(): AuthMethod {
    return (this.userRepositoryService.getAuthUser().preferredAuth as AuthMethod) ?? AuthMethod.EMAIL
  }

  public getScreenId(): number {
    return this.currentScreenId
  }

  public getRoleId(permissions: Array<Permission>, screenId: number): number {
    if (this.getPioneer()) return Role.ADMIN
    else return permissions.find((permission: Permission) => permission.screenId === screenId)?.roleId ?? Role.READER
  }

  public getPioneer(): boolean {
    return this.userRepositoryService.getAuthUser().pioneer
  }

  public logout(): void {
    this.pulsarWebSocketService.notifyLogoutBeforeTokenClear()
    if (!isExternalRoute(this.router.url) && !isExternalRoute()) this.router.navigate([SystemModules.home.uri])
    this.splashScreenService.hiddenSplashScreen()
    this.logoutClearStorage()
    document.title = 'Octalink'
  }

  private logoutClearStorage(): void {
    localStorage.removeItem('ng2Idle.main.expiry')
    localStorage.removeItem(StorageKeys.KEY_THEME)
    localStorage.removeItem(StorageKeys.KEY_LOGGED_USER)
    localStorage.removeItem(StorageKeys.KEY_LOGGED_USER_COMPANIES)
    localStorage.removeItem(StorageKeys.KEY_REFRESH_TOKEN)
    localStorage.removeItem(StorageKeys.KEY_EXPIRATION_TOKEN)
    localStorage.removeItem(StorageKeys.KEY_TOKEN)
    localStorage.removeItem(StorageKeys.KEY_2FA_SECRET_URI)
    sessionStorage.removeItem(StorageKeys.KEY_COMPANY)
    sessionStorage.removeItem(StorageKeys.KEY_COMPANY_BRANCHES)
    localStorage.removeItem(StorageKeys.KEY_REFRESH_TOKEN_EXPIRATION_TIME)
  }

  public isLogged(): boolean {
    return this.tokenService.hasToken()
  }

  public getIPAddress(): Observable<Array<any>> {
    return this.userRepositoryService.getIPAddress().pipe(
      map((res: HttpResponse<any>) => res.body.ip),
      catchError((httpError: HttpErrorResponse, caught: Observable<any>) => {
        this.toastService.sendToast({ severity: ToastSeverity.ERROR, message: 'Falha ao recuperar o enderço de IP do usuário!' } as Toast)
        return new Array()
      })
    )
  }

  public getGeolocation(): Observable<GeolocationPosition> {
    return new Observable<GeolocationPosition>((subscriber: Subscriber<GeolocationPosition>) => {
      navigator.geolocation.watchPosition(
        (GeolocationPosition) => {
          subscriber.next(GeolocationPosition)
          subscriber.complete()
        },
        (GeolocationPositionError) => subscriber.error(GeolocationPositionError)
      )
    })
  }

  public getAuthUserPermissions(): Observable<Array<string>> {
    const userId: number = this.getAuthUserId()

    return this.userRepositoryService.getAuthUserPermissions(userId).pipe(
      map((res: HttpResponse<ApiResponse>) => {
        const companiesData: Array<any> = res.body?.data.companies ?? []
        const companies: Array<AuthUserCompany> = companiesData.map((company: any) => new AuthUserCompany(company))
        this.companies = companies
        this.companyService.ensureSelectedCompany(companies)
        this.companiesBehaviorSubject.next(companies)
        return companies
      }),
      catchError((httpError: HttpErrorResponse, caught: Observable<any>) => {
        this.companiesBehaviorSubject.next(new Array())
        this.companies = []
        return []
      })
    )
  }
}
